<template>
  <div class="canvas-container" ref="canvasContainer">
    <!-- P4-17 — background-load overlay: disables the canvas + shows a spinner
         while a new upload / exercise / remote background image is loading. -->
    <div v-if="isBackgroundLoading" class="bg-loading-overlay">
      <div class="bg-loading-card">
        <div class="bg-loading-spinner"></div>
        <p class="bg-loading-text">Yeni Görsel Yükleniyor</p>
      </div>
    </div>
    <!-- Sidebar Toggle Button -->
    <button @click="toggleSidebars" class="sidebar-toggle" title="Toggle Sidebars">
      <i class="fas fa-bars" v-if="sidebarVisible"></i>
      <i class="fas fa-expand-arrows-alt" v-else></i>
    </button>
    <!-- Canvas Controls Toggle Button -->
    <button
      @click="toggleControls"
      class="controls-toggle"
      title="Toggle Canvas Controls"
      v-if="!isGuest"
    >
      <i class="fas fa-eye" v-if="showControls"></i>
      <i class="fas fa-eye-slash" v-else></i>
    </button>

    <!-- Background Upload Controls -->
    <div
      class="background-controls"
      :class="{ hidden: !showControls }"
      v-if="!isGuest && permissions.canUploadBackground"
    >
      <input
        type="file"
        ref="fileInput"
        @change="handleFileUpload"
        accept="image/*,.pdf"
        style="display: none"
      />
      <button
        @click="triggerFileUpload"
        class="control-btn upload-btn"
        title="Upload Background"
      >
        <i class="fas fa-image"></i>
      </button>
      <button
        v-if="backgroundImage"
        @click="clearBackground"
        class="control-btn clear-btn"
        title="Clear Background"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
    <!-- Zoom Controls -->
    <div class="zoom-controls" :class="{ hidden: !showControls }" v-if="!isGuest">
      <button @click="zoomIn" class="control-btn zoom-btn" title="Zoom In">
        <i class="fas fa-plus"></i>
      </button>
      <span class="zoom-level"
        >{{
          Math.round((backgroundImage ? contentZoom : stageConfig.scaleX) * 100)
        }}%</span
      >
      <button @click="zoomOut" class="control-btn zoom-btn" title="Zoom Out">
        <i class="fas fa-minus"></i>
      </button>
      <button
        @click="resetZoom"
        class="control-btn zoom-btn reset-btn"
        title="Reset Zoom"
      >
        <i class="fas fa-expand-arrows-alt"></i>
      </button>
      <!-- <button
        @click="toggleFullscreen"
        class="control-btn fullscreen-btn"
        title="Toggle Fullscreen"
      >
        <i class="fas fa-expand"></i>
      </button> -->
      <!-- PDF Pagination Controls: Only show if PDF is uploaded -->
      <div
        v-if="isPdfBackground"
        class="pdf-pagination"
        style="display: inline-flex; align-items: center; margin-left: 8px"
      >
        <button
          :disabled="pdfPage <= 1"
          @click="changePdfPage(pdfPage - 1)"
          title="Previous Page"
          style="margin-right: 4px"
          class="control-btn zoom-btn reset-btn"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <span style="font-size: 12px">{{ pdfPage }} / {{ pdfTotalPages }}</span>
        <button
          :disabled="pdfPage >= pdfTotalPages"
          @click="changePdfPage(pdfPage + 1)"
          title="Next Page"
          style="margin-left: 4px"
          class="control-btn zoom-btn reset-btn"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
    <!-- Konva Stage -->
    <konva-stage
      ref="stage"
      :config="stageConfig"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @wheel="handleWheel"
      @dragend="handleDragEnd"
    >
      <!-- Background and Drawing Layer (same coordinate space) -->
      <konva-layer ref="drawingLayer" :config="drawingLayerConfig">
        <!-- Frame background area (to show the gap) -->
        <konva-rect
          v-if="backgroundImage && frameBackgroundConfig"
          :config="frameBackgroundConfig"
        />
        <!-- Clipping Group for drawable area -->
        <konva-group v-if="backgroundImage" :config="clippingGroupConfig">
          <!-- Background Image -->
          <konva-image v-if="backgroundImage" :config="backgroundImageConfig" />
          <!-- Drawn Lines -->
          <konva-line v-for="line in lines" :key="line.id" :config="line" />
          <!-- Drawn Rectangles -->
          <konva-rect v-for="rect in rectangles" :key="rect.id" :config="rect" />
          <!-- Drawn Circles -->
          <konva-circle v-for="circle in circles" :key="circle.id" :config="circle" />
          <!-- Current drawing shape (preview) -->
          <konva-line v-if="currentLine" :config="currentLine" />
          <konva-rect v-if="currentRect" :config="currentRect" />
          <konva-circle v-if="currentCircle" :config="currentCircle" />
        </konva-group>
        <!-- Fallback for when no background image (no clipping) -->
        <template v-if="!backgroundImage">
          <!-- Drawn Lines -->
          <konva-line v-for="line in lines" :key="line.id" :config="line" />
          <!-- Drawn Rectangles -->
          <konva-rect v-for="rect in rectangles" :key="rect.id" :config="rect" />
          <!-- Drawn Circles -->
          <konva-circle v-for="circle in circles" :key="circle.id" :config="circle" />
          <!-- Current drawing shape (preview) -->
          <konva-line v-if="currentLine" :config="currentLine" />
          <konva-rect v-if="currentRect" :config="currentRect" />
          <konva-circle v-if="currentCircle" :config="currentCircle" />
        </template>
      </konva-layer>
      <!-- Frame Layer (always on top and not affected by content zoom) -->
      <konva-layer ref="frameLayer" :config="frameLayerConfig">
        <!-- Hard Frame around background image -->
        <konva-rect v-if="backgroundImage && hardFrameConfig" :config="hardFrameConfig" />
      </konva-layer>
      <!-- Mask Layer to hide empty space outside the frame -->
      <konva-layer v-if="backgroundImage" ref="maskLayer">
        <konva-rect v-for="(mask, index) in maskConfigs" :key="'mask-'+index" :config="mask" />
      </konva-layer>
    </konva-stage>
    <div id="pageContainer">
      <div id="viewer" class="pdfViewer"></div>
    </div>
    <!-- Frame Violation Feedback -->
    <div v-if="showFrameViolation" class="frame-violation-feedback">
      <i class="fas fa-exclamation-triangle"></i> Çizim yalnızca görsel alanı ile
      sınırlıdır
    </div>
  </div>
</template>
<script>
// P1-7 — shared axios instance (the `@axios` alias points at client/src/libs/
// axios.js). Used to upload the background image to the server.
import axios from "@axios";

export default {
  name: "CanvasStage",
  props: {
    // P1-7 — the session/room uuid, threaded from HostView/GuestView, so the
    // background can be uploaded to POST /online/:roomId/background.
    roomId: {
      type: String,
      default: null,
    },
    activeTool: {
      type: String,
      default: "pen",
    },
    toolOptions: {
      type: Object,
      default: () => ({}),
    },
    sidebarVisible: {
      type: Boolean,
      default: true,
    },
    isGuest: {
      type: Boolean,
      default: false,
    },
    permissions: {
      type: Object,
      default: () => ({
        canUploadBackground: true,
        canClear: true,
      }),
    },
  },
  data() {
    return {
      maxChunks: 0,
      receivedChunks: [],
      // Per-transfer chunk buffers, keyed by transferId, so overlapping or
      // duplicated image transfers can't corrupt one another (P0-3).
      transfers: {},
      // UI state
      showControls: true,

      // PDF pagination state
      isPdfBackground: false,
      pdfPage: 1,
      pdfTotalPages: 1,
      pdfDoc: null,

      // Background-based coordinate system
      backgroundDimensions: {
        width: 1920, // Default or actual background width
        height: 1080, // Default or actual background height
        naturalWidth: 1920, // Original image dimensions
        naturalHeight: 1080,
      },
      // Actual stage config - adapts to container
      stageConfig: {
        width: 800,
        height: 600,
        scaleX: 1,
        scaleY: 1,
        x: 0,
        y: 0,
        draggable: true,
      },
      // Background scale and position
      backgroundScale: 1,
      backgroundOffset: { x: 0, y: 0 },
      // Internal zoom for content within frame
      contentZoom: 1,
      contentOffset: { x: 0, y: 0 },
      isDrawing: false,
      // P4-17 — disables the canvas + shows a "Yeni Görsel Yükleniyor" spinner
      // while a new background image (upload / exercise / remote) is loading.
      isBackgroundLoading: false,
      lines: [],
      rectangles: [],
      circles: [],
      currentLine: null,
      currentRect: null,
      currentCircle: null,
      startPos: null,

      // Undo/Redo history management
      canvasHistory: [],
      currentHistoryStep: -1,
      maxHistorySteps: 50,
      backgroundImage: null,
      backgroundImageConfig: null,
      frameBackgroundConfig: null,
      hardFrameConfig: null,
      clippingGroupConfig: {
        clipX: 0,
        clipY: 0,
        clipWidth: 200,
        clipHeight: 200,
        clip: {
          x: 0,
          y: 0,
          width: 200,
          height: 200,
        },
      },
      frameThickness: 6, // Thickness of the hard frame border (increased for visibility)
      frameGap: 0, // Gap between image and frame border
      frameOuterPadding: 0, // Additional padding outside the frame
      showFrameViolation: false, // Show visual feedback when trying to draw outside frame
      lastCenter: null,
      lastDist: 0,
      dragStopped: false,

      // Content dragging state
      isContentDragging: false,
      contentDragStart: null,
      initialContentOffset: null,
      dragSensitivity: 1, // Sensitivity factor for smoother dragging (0.5 = half speed)
      lastZoomEmit: 0,
      zoomEmitTimer: null,
    };
  },
  computed: {
    stage() {
      return this.$refs.stage?.getNode();
    },
    // Drawing layer scales with background and includes content zoom
    drawingLayerConfig() {
      if (this.backgroundImage) {
        // When there's a background image, apply both background scale and content zoom
        return {
          scaleX: this.backgroundScale * this.contentZoom,
          scaleY: this.backgroundScale * this.contentZoom,
          x: this.backgroundOffset.x + this.contentOffset.x * this.backgroundScale,
          y: this.backgroundOffset.y + this.contentOffset.y * this.backgroundScale,
          clipX: 0,
          clipY: 0,
          clipWidth: this.backgroundDimensions.width,
          clipHeight: this.backgroundDimensions.height,
        };
      } else {
        // When no background image, use original behavior
        return {
          scaleX: this.backgroundScale,
          scaleY: this.backgroundScale,
          x: this.backgroundOffset.x,
          y: this.backgroundOffset.y,
        };
      }
    },
    // Frame layer config - always at background scale and offset, not affected by content zoom
    frameLayerConfig() {
      return {
        scaleX: this.backgroundScale,
        scaleY: this.backgroundScale,
        x: this.backgroundOffset.x,
        y: this.backgroundOffset.y,
      };
    },
    maskConfigs() {
      if (!this.backgroundImage) return [];
      
      const stageW = this.stageConfig.width;
      const stageH = this.stageConfig.height;
      const imgW = this.backgroundDimensions.width * this.backgroundScale;
      const imgH = this.backgroundDimensions.height * this.backgroundScale;
      const offX = this.backgroundOffset.x;
      const offY = this.backgroundOffset.y;
      const color = '#ffffff';

      return [
        // Top Mask
        { x: 0, y: 0, width: stageW, height: Math.max(0, offY), fill: color, listening: false },
        // Bottom Mask
        { x: 0, y: offY + imgH, width: stageW, height: Math.max(0, stageH - (offY + imgH)), fill: color, listening: false },
        // Left Mask
        { x: 0, y: offY, width: Math.max(0, offX), height: imgH, fill: color, listening: false },
        // Right Mask
        { x: offX + imgW, y: offY, width: Math.max(0, stageW - (offX + imgW)), height: imgH, fill: color, listening: false }
      ];
    },
  },
  watch: {
    activeTool(newTool) {
      // Disable stage dragging when there's a background image (content dragging is handled separately)
      if (this.backgroundImage && this.stage) {
        this.stage.draggable(false);
        return;
      }

      // Only allow stage dragging if user has permission and using select tool (when no background image)
      if (newTool === "select" && this.stage) {
        const canDrag =
          !this.isGuest || (this.permissions && this.permissions.canDragCanvas);
        this.stage.draggable(canDrag);
      } else if (this.stage) {
        // For non-select tools, disable dragging for guests without permission
        const canDrag =
          !this.isGuest || (this.permissions && this.permissions.canDragCanvas);
        this.stage.draggable(canDrag && newTool === "select");
      }
    },
  },
  mounted() {
    // PDF.js is now bundled locally (pdfjs-dist) and lazy-loaded on demand in
    // loadPdfLib() — no external CDN, so PDFs work even behind strict/mobile
    // firewalls where cdnjs was unreachable. The worker is served from our own
    // origin (public/pdf.worker.min.js).
    this.$nextTick(() => {
      this.initializeStage();
    });

    // Simple resize handler
    window.addEventListener("resize", this.updateStageSize);

    // Disable context menu on canvas
    this.$refs.stage?.$el.addEventListener("contextmenu", (e) => {
      e.preventDefault();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateStageSize);
  },
  methods: {
    updateStageSize() {
      if (!this.$refs.canvasContainer) return;

      const container = this.$refs.canvasContainer;
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      // Update actual stage size
      this.stageConfig.width = containerWidth;
      this.stageConfig.height = containerHeight;

      // Calculate how to fit background in container
      this.calculateBackgroundScale();
    },

    calculateBackgroundScale() {
      if (!this.stageConfig.width || !this.stageConfig.height) return;

      const containerWidth = this.stageConfig.width;
      const containerHeight = this.stageConfig.height;

      // Calculate scale to fit background in container while maintaining aspect ratio
      const scaleX = containerWidth / this.backgroundDimensions.width;
      const scaleY = containerHeight / this.backgroundDimensions.height;

      // Use the smaller scale to ensure background fits completely
      this.backgroundScale = Math.min(scaleX, scaleY);

      // Center the background in the container
      const scaledWidth = this.backgroundDimensions.width * this.backgroundScale;
      const scaledHeight = this.backgroundDimensions.height * this.backgroundScale;

      this.backgroundOffset = {
        x: (containerWidth - scaledWidth) / 2,
        y: (containerHeight - scaledHeight) / 2,
      };

      // Update background image config if exists
      if (this.backgroundImage && this.backgroundImageConfig) {
        this.updateBackgroundImageSize();
      }
    },

    initializeStage() {
      if (!this.$refs.canvasContainer) return;

      const container = this.$refs.canvasContainer;
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      // Set initial stage size
      this.stageConfig.width = containerWidth;
      this.stageConfig.height = containerHeight;

      // Calculate initial background scale
      this.calculateBackgroundScale();

      // Initialize undo/redo history
      this.initializeHistory();

      // Set dragging permission based on user role and tool
      if (this.stage) {
        // Disable stage dragging when there's a background image (content dragging is handled separately)
        if (this.backgroundImage) {
          this.stage.draggable(false);
        } else {
          const canDrag =
            !this.isGuest || (this.permissions && this.permissions.canDragCanvas);
          this.stage.draggable(canDrag && this.activeTool === "select");
        }
      }
    },

    updateBackgroundImageSize() {
      if (!this.backgroundImage) return;
      // Position and scale background image to fill the background area with content zoom
      this.backgroundImageConfig = {
        image: this.backgroundImage,
        width: this.backgroundDimensions.width,
        height: this.backgroundDimensions.height,
        crop: {
          // x: frameCenter.x, //bunu ayarlarsak cozeriz
          // y: frameCenter.y,
          width: this.backgroundDimensions.width,
          height: this.backgroundDimensions.height,
        },
        listening: false,
      };

      // Create a subtle background for the frame area to show the gap
      this.frameBackgroundConfig = {
        x: 0,
        y: 0,
        width: this.backgroundDimensions.width,
        height: this.backgroundDimensions.height,
        fill: "",
        stroke: "#e9ecef",
        strokeWidth: 1,
        listening: false,
        opacity: 0.8,
      };

      // Create hard frame configuration (always fixed)
      this.hardFrameConfig = {
        x: 0,
        y: 0,
        width: this.backgroundDimensions.width,
        height: this.backgroundDimensions.height,
        stroke: "#00cfe8",
        strokeWidth: this.frameThickness,
        fill: "transparent",
        listening: false,
        shadowColor: "rgba(0, 207, 232, 0.3)",
        shadowBlur: 6,
        shadowOffset: { x: 2, y: 2 },
      };

      // Create clipping group configuration to restrict drawing within the image area
      this.clippingGroupConfig = {
        clipX: 0,
        clipY: 0,
        clipWidth: this.backgroundDimensions.width,
        clipHeight: this.backgroundDimensions.height,
      };

      // Debug log
      /* console.log("Background image updated:", {
        dimensions: this.backgroundDimensions,
        scale: this.backgroundScale,
        offset: this.backgroundOffset,
        contentZoom: this.contentZoom,
        contentOffset: this.contentOffset,
        frameBackground: this.frameBackgroundConfig,
        frame: this.hardFrameConfig,
        clipping: this.clippingGroupConfig,
      }); */

      // Disable stage dragging when background image is present (content dragging handled separately)
      if (this.stage) {
        this.stage.draggable(false);
      }
    },

    getPointerPosition(e) {
      if (e.evt && e.evt.touches && e.evt.touches.length > 0) {
        // Touch event
        const touch = e.evt.touches[0];
        const rect = this.stage.container().getBoundingClientRect();
        const screenPos = {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        };
        return this.screenToBackground(screenPos);
      } else {
        // Mouse event
        const screenPos = this.stage.getPointerPosition();
        return this.screenToBackground(screenPos);
      }
    },

    // Check if a point is within the hard frame boundaries
    isPointWithinFrame(point) {
      if (!this.backgroundImage || !point) return true; // No frame restrictions when no image

      return (
        point.x >= 0 &&
        point.x <= this.backgroundDimensions.width &&
        point.y >= 0 &&
        point.y <= this.backgroundDimensions.height
      );
    },

    // Clamp a point to be within the frame boundaries
    clampPointToFrame(point) {
      if (!this.backgroundImage || !point) return point;

      return {
        x: Math.max(0, Math.min(this.backgroundDimensions.width, point.x)),
        y: Math.max(0, Math.min(this.backgroundDimensions.height, point.y)),
      };
    },

    // Show visual feedback when trying to draw outside frame
    showFrameViolationFeedback() {
      if (!this.backgroundImage) return;

      this.showFrameViolation = true;
      // Hide the feedback after a short time
      setTimeout(() => {
        this.showFrameViolation = false;
      }, 500);
    },

    // Constrain content offset to keep content within frame bounds
    constrainContentOffset() {
      if (!this.backgroundImage) return;

      const frameWidth = this.backgroundDimensions.width;
      const frameHeight = this.backgroundDimensions.height;
      const contentWidth = frameWidth * this.contentZoom;
      const contentHeight = frameHeight * this.contentZoom;

      // Calculate maximum offset bounds
      const maxOffsetX = Math.max(0, contentWidth - frameWidth);
      const maxOffsetY = Math.max(0, contentHeight - frameHeight);

      // Constrain offsets
      this.contentOffset.x = Math.max(-maxOffsetX, Math.min(0, this.contentOffset.x));
      this.contentOffset.y = Math.max(-maxOffsetY, Math.min(0, this.contentOffset.y));
    },

    // Emit content zoom update for synchronization (Host only)
    emitContentZoomUpdate(force = false) {
      if (this.isGuest) return; // Guests should not emit zoom updates

      const now = Date.now();
      if (!force && now - this.lastZoomEmit < 50) {
        if (this.zoomEmitTimer) return;
        this.zoomEmitTimer = setTimeout(() => {
          this.zoomEmitTimer = null;
          this.emitContentZoomUpdate(true);
        }, 50);
        return;
      }

      if (this.zoomEmitTimer) {
        clearTimeout(this.zoomEmitTimer);
        this.zoomEmitTimer = null;
      }

      this.lastZoomEmit = now;

      const zoomData = {
        contentZoom: this.contentZoom,
        contentOffset: { ...this.contentOffset },
        timestamp: now,
      };

      // console.log("Emitting content zoom update:", zoomData);
      this.$emit("zoom-updated", zoomData);
    },

    // Apply content zoom update from host (Guest only)
    applyContentZoomUpdate(zoomData) {
      if (!this.isGuest) {
        return; // Only guests should apply zoom updates from host
      }

      console.log("Applying content zoom update from host:", zoomData);

      // Update content zoom and offset
      this.contentZoom = zoomData.contentZoom || 1;
      this.contentOffset = zoomData.contentOffset || { x: 0, y: 0 };

      // Update background image size and drawing layer
      this.updateBackgroundImageSize();

      // Force canvas redraw
      this.$nextTick(() => {
        if (this.$refs.drawingLayer) {
          this.$refs.drawingLayer.getNode().batchDraw();
        }
      });
    },

    // Check if stage dragging should be restricted by frame boundaries
    isStagePositionWithinFrame(stagePos) {
      if (!this.backgroundImage) return true; // No restrictions when no image

      // Calculate the viewport boundaries in background coordinate space
      const viewportWidth = this.stageConfig.width / this.stageConfig.scaleX;
      const viewportHeight = this.stageConfig.height / this.stageConfig.scaleY;

      // Convert stage position to background coordinate space
      const backgroundViewX = -stagePos.x / this.stageConfig.scaleX;
      const backgroundViewY = -stagePos.y / this.stageConfig.scaleY;

      // Check if the viewport shows area outside the background boundaries
      const rightEdge = backgroundViewX + viewportWidth;
      const bottomEdge = backgroundViewY + viewportHeight;

      // Allow viewing but prevent going too far beyond
      const buffer = 20; // Allow some extra

      return (
        backgroundViewX >= -buffer &&
        backgroundViewY >= -buffer &&
        rightEdge <= this.backgroundDimensions.width + buffer &&
        bottomEdge <= this.backgroundDimensions.height + buffer
      );
    },

    screenToBackground(screenPos) {
      // Convert screen coordinates to background-relative coordinates
      if (!this.backgroundImage) {
        // When no background image, use original behavior
        const stageTransform = this.stage.getAbsoluteTransform().copy();
        stageTransform.invert();
        const stagePos = stageTransform.point(screenPos);

        const backgroundPos = {
          x: (stagePos.x - this.backgroundOffset.x) / this.backgroundScale,
          y: (stagePos.y - this.backgroundOffset.y) / this.backgroundScale,
        };
        return backgroundPos;
      }

      // When there's a background image, account for content zoom and offset
      const stageTransform = this.stage.getAbsoluteTransform().copy();
      stageTransform.invert();
      const stagePos = stageTransform.point(screenPos);

      // Reverse the drawing layer transform (background scale, content zoom, and offsets)
      const totalScale = this.backgroundScale * this.contentZoom;
      const totalOffsetX =
        this.backgroundOffset.x + this.contentOffset.x * this.backgroundScale;
      const totalOffsetY =
        this.backgroundOffset.y + this.contentOffset.y * this.backgroundScale;

      const backgroundPos = {
        x: (stagePos.x - totalOffsetX) / totalScale,
        y: (stagePos.y - totalOffsetY) / totalScale,
      };

      return backgroundPos;
    },

    backgroundToScreen(backgroundPos) {
      // Convert background coordinates to screen coordinates
      if (!this.backgroundImage) {
        // When no background image, use original behavior
        const stagePos = {
          x: backgroundPos.x * this.backgroundScale + this.backgroundOffset.x,
          y: backgroundPos.y * this.backgroundScale + this.backgroundOffset.y,
        };

        const stageTransform = this.stage.getAbsoluteTransform();
        return stageTransform.point(stagePos);
      }

      // When there's a background image, account for content zoom and offset
      const totalScale = this.backgroundScale * this.contentZoom;
      const totalOffsetX =
        this.backgroundOffset.x + this.contentOffset.x * this.backgroundScale;
      const totalOffsetY =
        this.backgroundOffset.y + this.contentOffset.y * this.backgroundScale;

      const stagePos = {
        x: backgroundPos.x * totalScale + totalOffsetX,
        y: backgroundPos.y * totalScale + totalOffsetY,
      };

      const stageTransform = this.stage.getAbsoluteTransform();
      return stageTransform.point(stagePos);
    },

    handleWheel(e) {
      // Prevent default scroll behavior
      e.evt.preventDefault();

      // Only host can control zoom, guests should not zoom on their own
      if (this.isGuest) {
        return;
      }

      // If no background image, use original stage zoom behavior
      if (!this.backgroundImage) {
        const scaleBy = 1.05;
        const stage = this.stage;
        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();

        const mousePointTo = {
          x: (pointer.x - stage.x()) / oldScale,
          y: (pointer.y - stage.y()) / oldScale,
        };

        let newScale = e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
        newScale = Math.max(0.1, Math.min(5, newScale));

        stage.scale({ x: newScale, y: newScale });

        const newPos = {
          x: pointer.x - mousePointTo.x * newScale,
          y: pointer.y - mousePointTo.y * newScale,
        };
        stage.position(newPos);

        this.stageConfig.scaleX = newScale;
        this.stageConfig.scaleY = newScale;
        this.stageConfig.x = newPos.x;
        this.stageConfig.y = newPos.y;

        this.emitZoomUpdate();
        return;
      }

      // Content zoom within hard frame
      const scaleBy = 1.05;
      const pointer = this.stage.getPointerPosition();

      // Convert pointer to frame coordinate space
      const framePointer = this.screenToBackground(pointer);

      // Only zoom if pointer is within the frame area
      if (!this.isPointWithinFrame(framePointer)) {
        return;
      }

      const oldZoom = this.contentZoom;
      let newZoom = e.evt.deltaY > 0 ? oldZoom / scaleBy : oldZoom * scaleBy;

      // Limit content zoom levels (minimum 100%, maximum 300%)
      newZoom = Math.max(1.0, Math.min(3, newZoom));

      // Calculate the point in content space that should remain fixed
      const contentPoint = {
        x: framePointer.x - this.contentOffset.x,
        y: framePointer.y - this.contentOffset.y,
      };

      // Update content zoom
      this.contentZoom = newZoom;

      // Adjust content offset to keep the zoom point fixed
      this.contentOffset = {
        x: framePointer.x - contentPoint.x,
        y: framePointer.y - contentPoint.y,
      };

      // Constrain content offset to keep content within frame bounds
      this.constrainContentOffset();

      // Update background image size and drawing layer
      this.updateBackgroundImageSize();

      // Emit zoom update for synchronization
      this.emitContentZoomUpdate();
    },

    handleDragEnd() {
      // Update position after drag
      const stage = this.stage;
      let newX = stage.x();
      let newY = stage.y();

      // If we have a background image, content dragging is handled separately
      // Stage dragging should be disabled when there's a background image
      if (this.backgroundImage) {
        // Reset stage position to prevent any stage movement when there's a background
        stage.position({ x: 0, y: 0 });
        this.stageConfig.x = 0;
        this.stageConfig.y = 0;
        return;
      }

      // Original stage dragging behavior for when there's no background image
      this.stageConfig.x = newX;
      this.stageConfig.y = newY;

      // Emit zoom update for synchronization when host drags
      if (!this.isGuest) {
        this.emitZoomUpdate();
      }
    },

    zoomIn() {
      // Only host can control zoom
      if (this.isGuest) {
        return;
      }

      // If no background image, use original stage zoom behavior
      if (!this.backgroundImage) {
        const stage = this.stage;
        const oldScale = stage.scaleX();
        const newScale = Math.min(5, oldScale * 1.2);

        const center = {
          x: this.stageConfig.width / 2,
          y: this.stageConfig.height / 2,
        };

        const mousePointTo = {
          x: (center.x - stage.x()) / oldScale,
          y: (center.y - stage.y()) / oldScale,
        };

        stage.scale({ x: newScale, y: newScale });

        const newPos = {
          x: center.x - mousePointTo.x * newScale,
          y: center.y - mousePointTo.y * newScale,
        };
        stage.position(newPos);

        this.stageConfig.scaleX = newScale;
        this.stageConfig.scaleY = newScale;
        this.stageConfig.x = newPos.x;
        this.stageConfig.y = newPos.y;

        this.emitZoomUpdate();
        return;
      }

      // Content zoom within hard frame
      const oldZoom = this.contentZoom;
      const newZoom = Math.min(3, oldZoom * 1.2);

      // Calculate center of frame for zoom focal point
      const frameCenter = {
        x: this.backgroundDimensions.width / 2,
        y: this.backgroundDimensions.height / 2,
      };

      // Calculate the point in content space that should remain fixed
      const contentPoint = {
        x: frameCenter.x - this.contentOffset.x,
        y: frameCenter.y - this.contentOffset.y,
      };

      // Update content zoom
      this.contentZoom = newZoom;

      // Adjust content offset to keep the zoom point fixed
      this.contentOffset = {
        x: frameCenter.x - contentPoint.x,
        y: frameCenter.y - contentPoint.y,
      };

      // Constrain content offset to keep content within frame bounds
      this.constrainContentOffset();

      // Update background image size and drawing layer
      this.updateBackgroundImageSize();

      // Emit zoom update for synchronization
      this.emitContentZoomUpdate();
    },

    zoomOut() {
      // Only host can control zoom
      if (this.isGuest) {
        return;
      }

      // If no background image, use original stage zoom behavior
      if (!this.backgroundImage) {
        const stage = this.stage;
        const oldScale = stage.scaleX();
        const newScale = Math.max(0.1, oldScale / 1.2);

        const center = {
          x: this.stageConfig.width / 2,
          y: this.stageConfig.height / 2,
        };

        const mousePointTo = {
          x: (center.x - stage.x()) / oldScale,
          y: (center.y - stage.y()) / oldScale,
        };

        stage.scale({ x: newScale, y: newScale });

        const newPos = {
          x: center.x - mousePointTo.x * newScale,
          y: center.y - mousePointTo.y * newScale,
        };
        stage.position(newPos);

        this.stageConfig.scaleX = newScale;
        this.stageConfig.scaleY = newScale;
        this.stageConfig.x = newPos.x;
        this.stageConfig.y = newPos.y;

        this.emitZoomUpdate();
        return;
      }

      // Content zoom within hard frame
      const oldZoom = this.contentZoom;
      const newZoom = Math.max(1.0, oldZoom / 1.2);

      // Calculate center of frame for zoom focal point
      const frameCenter = {
        x: this.backgroundDimensions.width / 2,
        y: this.backgroundDimensions.height / 2,
      };

      // Calculate the point in content space that should remain fixed
      const contentPoint = {
        x: frameCenter.x - this.contentOffset.x,
        y: frameCenter.y - this.contentOffset.y,
      };

      // Update content zoom
      this.contentZoom = newZoom;

      // Adjust content offset to keep the zoom point fixed
      this.contentOffset = {
        x: frameCenter.x - contentPoint.x,
        y: frameCenter.y - contentPoint.y,
      };

      // Constrain content offset to keep content within frame bounds
      this.constrainContentOffset();

      // Update background image size and drawing layer
      this.updateBackgroundImageSize();

      // Emit zoom update for synchronization
      this.emitContentZoomUpdate();
    },

    resetZoom() {
      // Only host can control zoom
      if (this.isGuest) {
        return;
      }

      // If no background image, use original stage zoom behavior
      if (!this.backgroundImage) {
        const stage = this.stage;
        stage.scale({ x: 1, y: 1 });
        stage.position({ x: 0, y: 0 });

        this.stageConfig.scaleX = 1;
        this.stageConfig.scaleY = 1;
        this.stageConfig.x = 0;
        this.stageConfig.y = 0;

        this.emitZoomUpdate();
        return;
      }

      // Reset content zoom within hard frame
      this.contentZoom = 1;
      this.contentOffset = { x: 0, y: 0 };

      // Update background image size and drawing layer
      this.updateBackgroundImageSize();

      // Emit zoom update for synchronization
      this.emitContentZoomUpdate();
    },

    handleMouseDown(e) {
      // Handle content dragging when there's a background image and using select tool
      if (this.backgroundImage && this.activeTool === "select") {
        const canDrag =
          !this.isGuest || (this.permissions && this.permissions.canDragCanvas);
        if (canDrag) {
          this.isContentDragging = true;
          this.contentDragStart = this.getPointerPosition(e);
          this.initialContentOffset = { ...this.contentOffset };
          return;
        }
      }

      // Only disable stage dragging when using drawing tools, not select tool
      if (this.activeTool !== "select") {
        this.stage.draggable(false);
      }

      // Only start drawing if not using select tool
      if (this.activeTool !== "select") {
        this.startDrawing(e);
      }
    },

    handleMouseMove(e) {
      // Handle content dragging movement
      if (this.isContentDragging && this.contentDragStart && this.backgroundImage) {
        const currentPos = this.getPointerPosition(e);
        const deltaX = (currentPos.x - this.contentDragStart.x) * this.dragSensitivity * this.contentZoom;
        const deltaY = (currentPos.y - this.contentDragStart.y) * this.dragSensitivity * this.contentZoom;

        // Update content offset with the drag delta (positive to make dragging feel natural)
        this.contentOffset.x = this.initialContentOffset.x + deltaX;
        this.contentOffset.y = this.initialContentOffset.y + deltaY;

        // Constrain content offset to frame boundaries
        this.constrainContentOffset();

        // Update background image size and drawing layer
        // this.updateBackgroundImageSize(); // Removed to prevent stutter

        // Emit content zoom update for synchronization
        this.emitContentZoomUpdate();
        return;
      }

      // Only continue drawing if not using select tool
      if (this.activeTool !== "select") {
        this.continueDrawing(e);
      }
    },

    handleMouseUp(e) {
      // End content dragging
      if (this.isContentDragging) {
        this.isContentDragging = false;
        this.contentDragStart = null;
        this.initialContentOffset = null;
        this.emitContentZoomUpdate(true);
        return;
      }

      // Re-enable stage dragging based on permissions and tool (only when no background image)
      if (!this.backgroundImage) {
        const canDrag =
          !this.isGuest || (this.permissions && this.permissions.canDragCanvas);

        if (this.activeTool === "select") {
          this.stage.draggable(canDrag);
        } else {
          this.stage.draggable(canDrag && this.activeTool === "select");
        }
      }

      // Finish drawing if using drawing tools
      if (this.activeTool !== "select") {
        this.finishDrawing(e);
      }
    },

    handleTouchStart(e) {
      e.evt.preventDefault();

      // Handle multi-touch for pinch zoom
      if (e.evt.touches.length === 2) {
        this.handlePinchStart(e);
        return;
      }

      // Single touch
      if (e.evt.touches.length === 1) {
        // Handle content dragging when there's a background image and using select tool
        if (this.backgroundImage && this.activeTool === "select") {
          const canDrag =
            !this.isGuest || (this.permissions && this.permissions.canDragCanvas);
          if (canDrag) {
            this.isContentDragging = true;
            this.contentDragStart = this.getPointerPosition(e);
            this.initialContentOffset = { ...this.contentOffset };
            return;
          }
        }

        // Only disable stage dragging when using drawing tools, and respect permissions
        if (this.activeTool !== "select") {
          this.stage.draggable(false);
          this.startDrawing(e);
        }
      }
    },

    handleTouchMove(e) {
      e.evt.preventDefault();

      // Handle multi-touch for pinch zoom
      if (e.evt.touches.length === 2) {
        this.handlePinchMove(e);
        return;
      }

      // Single touch
      if (e.evt.touches.length === 1) {
        // Handle content dragging movement
        if (this.isContentDragging && this.contentDragStart && this.backgroundImage) {
          const currentPos = this.getPointerPosition(e);
          const deltaX = (currentPos.x - this.contentDragStart.x) * this.dragSensitivity * this.contentZoom;
          const deltaY = (currentPos.y - this.contentDragStart.y) * this.dragSensitivity * this.contentZoom;

          // Update content offset with the drag delta (positive to make dragging feel natural)
          this.contentOffset.x = this.initialContentOffset.x + deltaX;
          this.contentOffset.y = this.initialContentOffset.y + deltaY;

          // Constrain content offset to frame boundaries
          this.constrainContentOffset();

          // Update background image size and drawing layer
          // this.updateBackgroundImageSize(); // Removed to prevent stutter

          // Emit content zoom update for synchronization
          this.emitContentZoomUpdate();
          return;
        }

        // Continue drawing only if not using select tool
        if (this.activeTool !== "select") {
          this.continueDrawing(e);
        }
      }
    },

    handleTouchEnd(e) {
      e.evt.preventDefault();

      // End content dragging
      if (this.isContentDragging) {
        this.isContentDragging = false;
        this.contentDragStart = null;
        this.initialContentOffset = null;
        this.emitContentZoomUpdate(true);
        return;
      }

      // Handle end of pinch zoom
      if (this.lastCenter) {
        this.handlePinchEnd(e);
        return;
      }

      // Re-enable stage dragging based on permissions (only when no background image)
      if (!this.backgroundImage) {
        const canDrag =
          !this.isGuest || (this.permissions && this.permissions.canDragCanvas);
        this.stage.draggable(canDrag && this.activeTool === "select");
      }

      // Only finish drawing if not using select tool
      if (this.activeTool !== "select") {
        this.finishDrawing(e);
      }
    },

    handlePinchStart(e) {
      const touch1 = e.evt.touches[0];
      const touch2 = e.evt.touches[1];

      this.lastCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      this.lastDist = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      // Disable drawing during pinch
      this.isDrawing = false;
    },

    handlePinchMove(e) {
      if (!this.lastCenter) return;

      // Only host can control zoom
      if (this.isGuest) {
        return;
      }

      const touch1 = e.evt.touches[0];
      const touch2 = e.evt.touches[1];

      const newCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      const newDist = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      // If no background image, use original stage zoom behavior
      if (!this.backgroundImage) {
        const stage = this.stage;
        const oldScale = stage.scaleX();

        // Calculate zoom
        const scaleBy = newDist / this.lastDist;
        let newScale = oldScale * scaleBy;
        newScale = Math.max(0.1, Math.min(5, newScale));

        // Get container rect for proper positioning
        const rect = stage.container().getBoundingClientRect();
        const centerPoint = {
          x: newCenter.x - rect.left,
          y: newCenter.y - rect.top,
        };

        const mousePointTo = {
          x: (centerPoint.x - stage.x()) / oldScale,
          y: (centerPoint.y - stage.y()) / oldScale,
        };

        stage.scale({ x: newScale, y: newScale });

        const newPos = {
          x: centerPoint.x - mousePointTo.x * newScale,
          y: centerPoint.y - mousePointTo.y * newScale,
        };
        stage.position(newPos);

        // Update config
        this.stageConfig.scaleX = newScale;
        this.stageConfig.scaleY = newScale;
        this.stageConfig.x = newPos.x;
        this.stageConfig.y = newPos.y;

        this.lastDist = newDist;
        this.lastCenter = newCenter;

        this.emitZoomUpdate();
        return;
      }

      // Content zoom within hard frame for background images
      const rect = this.stage.container().getBoundingClientRect();
      const centerPoint = {
        x: newCenter.x - rect.left,
        y: newCenter.y - rect.top,
      };

      // Convert center point to frame coordinate space
      const frameCenter = this.screenToBackground(centerPoint);

      // Only zoom if center point is within the frame area
      if (!this.isPointWithinFrame(frameCenter)) {
        this.lastDist = newDist;
        this.lastCenter = newCenter;
        return;
      }

      const oldZoom = this.contentZoom;
      const scaleBy = newDist / this.lastDist;
      let newZoom = oldZoom * scaleBy;

      // Limit content zoom levels (minimum 100%, maximum 300%)
      newZoom = Math.max(1.0, Math.min(3, newZoom));

      // Calculate the point in content space that should remain fixed
      const contentPoint = {
        x: frameCenter.x - this.contentOffset.x,
        y: frameCenter.y - this.contentOffset.y,
      };

      // Update content zoom
      this.contentZoom = newZoom;

      // Adjust content offset to keep the zoom point fixed
      this.contentOffset = {
        x: frameCenter.x - contentPoint.x,
        y: frameCenter.y - contentPoint.y,
      };

      // Constrain content offset to keep content within frame bounds
      this.constrainContentOffset();

      // Update background image size and drawing layer
      this.updateBackgroundImageSize();

      this.lastDist = newDist;
      this.lastCenter = newCenter;

      // Emit zoom update for synchronization
      this.emitContentZoomUpdate();
    },

    handlePinchEnd(e) {
      this.lastCenter = null;
      this.lastDist = 0;
      this.emitContentZoomUpdate(true);
    },

    startDrawing(e) {
      // Don't start drawing if select tool is active
      if (this.activeTool === "select") {
        return;
      }

      // Check permissions for guests
      if (this.isGuest && this.permissions) {
        const hasPermission = this.checkToolPermission(this.activeTool);
        if (!hasPermission) {
          console.log("Guest does not have permission to use tool:", this.activeTool);
          return;
        }
      }

      this.isDrawing = true;
      const pos = this.getPointerPosition(e);

      // Clamp starting position to frame boundaries if background image exists
      const clampedPos = this.clampPointToFrame(pos);
      this.startPos = clampedPos;

      if (this.activeTool === "pen") {
        this.currentLine = {
          points: [clampedPos.x, clampedPos.y],
          stroke: this.toolOptions.pen?.stroke || "#000000",
          strokeWidth: this.toolOptions.pen?.strokeWidth || 2,
          lineCap: "round",
          lineJoin: "round",
        };
      } else if (this.activeTool === "rectangle") {
        this.currentRect = {
          x: clampedPos.x,
          y: clampedPos.y,
          width: 0,
          height: 0,
          stroke: this.toolOptions.rectangle?.stroke || "#000000",
          strokeWidth: this.toolOptions.rectangle?.strokeWidth || 2,
          fill: this.toolOptions.rectangle?.filled
            ? this.toolOptions.rectangle?.stroke || "#000000"
            : "transparent",
        };
      } else if (this.activeTool === "circle") {
        this.currentCircle = {
          x: clampedPos.x,
          y: clampedPos.y,
          radius: 0,
          stroke: this.toolOptions.circle?.stroke || "#000000",
          strokeWidth: this.toolOptions.circle?.strokeWidth || 2,
          fill: this.toolOptions.circle?.filled
            ? this.toolOptions.circle?.stroke || "#000000"
            : "transparent",
        };
      } else if (this.activeTool === "eraser") {
        // P4-19 — begin an eraser stroke. Snapshot the pre-erase state (host) so
        // the whole drag collapses into a single undo step, then erase whatever is
        // already under the pointer.
        this._erasedIds = [];
        if (!this.isGuest) this.ensureCurrentStateSaved();
        this.eraseAtPointer();
      }
    },

    continueDrawing(e) {
      if (!this.isDrawing) return;

      const pos = this.getPointerPosition(e);
      const clampedPos = this.clampPointToFrame(pos);

      // Show violation feedback if position had to be clamped
      if (this.backgroundImage && (pos.x !== clampedPos.x || pos.y !== clampedPos.y)) {
        this.showFrameViolationFeedback();
      }

      if (this.activeTool === "pen") {
        if (this.currentLine) {
          // Only add point if it's within frame boundaries
          if (this.isPointWithinFrame(pos)) {
            this.currentLine.points = this.currentLine.points.concat([
              clampedPos.x,
              clampedPos.y,
            ]);
          }
        }
      } else if (this.activeTool === "rectangle" && this.currentRect) {
        // Calculate rectangle dimensions, clamping to frame boundaries
        const endX = clampedPos.x;
        const endY = clampedPos.y;

        const rectX = Math.min(this.startPos.x, endX);
        const rectY = Math.min(this.startPos.y, endY);
        const rectWidth = Math.abs(endX - this.startPos.x);
        const rectHeight = Math.abs(endY - this.startPos.y);

        this.currentRect.x = rectX;
        this.currentRect.y = rectY;
        this.currentRect.width = rectWidth;
        this.currentRect.height = rectHeight;
      } else if (this.activeTool === "circle" && this.currentCircle) {
        // Calculate circle radius, respecting frame boundaries
        const dx = clampedPos.x - this.startPos.x;
        const dy = clampedPos.y - this.startPos.y;
        const radius = Math.sqrt(dx * dx + dy * dy);

        // Make sure circle doesn't extend beyond frame
        const maxRadiusX = Math.min(
          this.startPos.x,
          this.backgroundDimensions.width - this.startPos.x
        );
        const maxRadiusY = Math.min(
          this.startPos.y,
          this.backgroundDimensions.height - this.startPos.y
        );
        const maxRadius = this.backgroundImage
          ? Math.min(radius, maxRadiusX, maxRadiusY)
          : radius;

        this.currentCircle.radius = maxRadius;
      } else if (this.activeTool === "eraser") {
        // P4-19 — keep erasing elements the pointer passes over during the drag.
        this.eraseAtPointer();
      }
    },

    finishDrawing(e) {
      if (!this.isDrawing) return;

      this.isDrawing = false;

      if (this.activeTool === "pen") {
        if (this.currentLine && this.currentLine.points.length > 2) {
          const newLine = {
            ...this.currentLine,
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          };
          console.log("Adding line:", newLine);
          this.lines.push(newLine);
          this.saveToHistory();
        }
        this.currentLine = null;
      } else if (this.activeTool === "rectangle" && this.currentRect) {
        if (
          Math.abs(this.currentRect.width) > 5 &&
          Math.abs(this.currentRect.height) > 5
        ) {
          const newRect = {
            ...this.currentRect,
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          };
          console.log("Adding rectangle:", newRect);
          this.rectangles.push(newRect);
          this.saveToHistory();
        }
        this.currentRect = null;
      } else if (this.activeTool === "circle" && this.currentCircle) {
        if (this.currentCircle.radius > 5) {
          const newCircle = {
            ...this.currentCircle,
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          };
          console.log("Adding circle:", newCircle);
          this.circles.push(newCircle);
          this.saveToHistory();
        }
        this.currentCircle = null;
      } else if (this.activeTool === "eraser") {
        // P4-19 — finalize the eraser stroke: one history step (host) + one
        // reliable broadcast of every element removed during the drag.
        if (this._erasedIds && this._erasedIds.length > 0) {
          if (!this.isGuest) this.saveToHistory();
          this.emitElementDelete(this._erasedIds);
        }
        this._erasedIds = [];
      }

      console.log("Current canvas state after drawing:");
      console.log("Lines:", this.lines.length);
      console.log("Rectangles:", this.rectangles.length);
      console.log("Circles:", this.circles.length);
      console.log("Background dimensions:", this.backgroundDimensions);
      console.log("Background scale:", this.backgroundScale);
      console.log("Background offset:", this.backgroundOffset);

      this.emitCanvasUpdate();
    },
    HandleURLImage(url) {
      if (!url) return;
      // Reuse the P1-7 hardened loader: it sets `crossOrigin="anonymous"` (keeps the
      // canvas untainted for the host snapshot/compress path), applies the image's
      // natural dimensions, and broadcasts the change via `emitCanvasUpdate(true)`.
      // The URL is already absolute (`${baseURL}/images/<q>.jpg`), and `/images` now
      // sends `Cross-Origin-Resource-Policy: cross-origin` so the cross-origin load
      // succeeds. Guests receive the background through the canvas-state broadcast.
      this._applyLocalBackground(url, true);
    },

    // Undo/Redo History Management
    saveToHistory() {
      // Only host can manage history (guests don't have undo/redo permissions)
      if (this.isGuest) {
        return;
      }

      const currentState = {
        lines: JSON.parse(JSON.stringify(this.lines)),
        rectangles: JSON.parse(JSON.stringify(this.rectangles)),
        circles: JSON.parse(JSON.stringify(this.circles)),
        timestamp: Date.now(),
      };

      // Remove any steps after current position (when undoing then doing something new)
      this.canvasHistory = this.canvasHistory.slice(0, this.currentHistoryStep + 1);

      // Add new state to history
      this.canvasHistory.push(currentState);
      this.currentHistoryStep = this.canvasHistory.length - 1;

      // Limit history size
      if (this.canvasHistory.length > this.maxHistorySteps) {
        this.canvasHistory.shift();
        this.currentHistoryStep = this.canvasHistory.length - 1;
      }

      console.log(
        "Canvas state saved to history. Current step:",
        this.currentHistoryStep
      );
    },

    // Helper method to ensure current state is saved before making changes
    ensureCurrentStateSaved() {
      if (this.isGuest) {
        return;
      }

      // Check if current canvas state is different from last saved state
      if (this.canvasHistory.length === 0) {
        this.saveToHistory();
        return;
      }

      const currentState = {
        lines: this.lines,
        rectangles: this.rectangles,
        circles: this.circles,
      };

      const lastSavedState = this.canvasHistory[this.currentHistoryStep];

      // Compare current state with last saved state
      if (
        JSON.stringify(currentState) !==
        JSON.stringify({
          lines: lastSavedState.lines,
          rectangles: lastSavedState.rectangles,
          circles: lastSavedState.circles,
        })
      ) {
        this.saveToHistory();
      }
    },

    initializeHistory() {
      // Save initial empty state
      this.canvasHistory = [
        {
          lines: [],
          rectangles: [],
          circles: [],
          timestamp: Date.now(),
        },
      ];
      this.currentHistoryStep = 0;
    },

    undo() {
      // Only host can undo (guests don't have this permission)
      if (this.isGuest) {
        console.log("Guests cannot undo");
        return;
      }

      if (this.currentHistoryStep > 0) {
        this.currentHistoryStep--;
        this.restoreFromHistory();
        console.log("Undo performed. Current step:", this.currentHistoryStep);

        // Emit complete canvas state to sync with guests
        this.emitCompleteCanvasState();
      } else {
        console.log("Nothing to undo");
      }
    },

    redo() {
      // Only host can redo (guests don't have this permission)
      if (this.isGuest) {
        console.log("Guests cannot redo");
        return;
      }

      if (this.currentHistoryStep < this.canvasHistory.length - 1) {
        this.currentHistoryStep++;
        this.restoreFromHistory();
        console.log("Redo performed. Current step:", this.currentHistoryStep);

        // Emit complete canvas state to sync with guests
        this.emitCompleteCanvasState();
      } else {
        console.log("Nothing to redo");
      }
    },

    restoreFromHistory() {
      if (
        this.currentHistoryStep >= 0 &&
        this.currentHistoryStep < this.canvasHistory.length
      ) {
        const historyState = this.canvasHistory[this.currentHistoryStep];

        this.lines = JSON.parse(JSON.stringify(historyState.lines));
        this.rectangles = JSON.parse(JSON.stringify(historyState.rectangles));
        this.circles = JSON.parse(JSON.stringify(historyState.circles));

        // Force re-render
        this.$nextTick(() => {
          if (this.$refs.drawingLayer) {
            this.$refs.drawingLayer.getNode().batchDraw();
          }
        });

        console.log("Canvas restored from history step:", this.currentHistoryStep);
      }
    },

    canUndo() {
      return !this.isGuest && this.currentHistoryStep > 0;
    },

    canRedo() {
      return !this.isGuest && this.currentHistoryStep < this.canvasHistory.length - 1;
    },

    // Downscale + JPEG-encode a loaded image, returning BOTH a Blob (uploaded to
    // the server — P1-7) and a data URL (the P0-3 data-channel fallback used if
    // the upload fails). Compressing keeps the stored file a fraction of the
    // native-resolution size.
    _compressImage(img, maxDim = 2000, quality = 0.8) {
      const scale = Math.min(
        1,
        maxDim / Math.max(img.naturalWidth, img.naturalHeight)
      );
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      const dataUrl = c.toDataURL("image/jpeg", quality);
      return new Promise((resolve) => {
        c.toBlob(
          (blob) => resolve({ blob, dataUrl }),
          "image/jpeg",
          quality
        );
      });
    },

    // P1-7 — set the local background from a loaded <img> and broadcast it.
    // `emitCanvasUpdate(true)` emits `backgroundImage = <img>.src`; when that is a
    // server URL it is lightweight and rides the P1-5 socket relay, so no base64
    // image crosses the wire.
    _applyLocalBackground(src, crossOrigin) {
      // P4-17 — show the loading overlay from the moment we start fetching the new
      // background until it renders (or fails). Covers upload, exercise selection
      // (HandleURLImage) and PDF paths, which all funnel through here.
      this.isBackgroundLoading = true;
      return new Promise((resolve) => {
        const image = new Image();
        if (crossOrigin) image.crossOrigin = "anonymous";
        image.onload = () => {
          this.backgroundImage = image;
          this.backgroundDimensions = {
            width: image.naturalWidth,
            height: image.naturalHeight,
            naturalWidth: image.naturalWidth,
            naturalHeight: image.naturalHeight,
          };
          this.contentZoom = 1;
          this.contentOffset = { x: 0, y: 0 };
          this.calculateBackgroundScale();
          this.updateBackgroundImageSize();
          this.$nextTick(() => {
            if (this.stage) this.stage.batchDraw();
          });
          this.emitCanvasUpdate(true);
          this.isBackgroundLoading = false;
          resolve(true);
        };
        image.onerror = () => {
          this.isBackgroundLoading = false;
          resolve(false);
        };
        image.src = src;
      });
    },

    // P1-7 — upload a compressed background to the server and use the returned
    // URL. On any failure (endpoint unavailable, no roomId, network) fall back to
    // the P0-3 inline path: the compressed data URL over the WebRTC data channel.
    async uploadBackground(blob, fallbackDataUrl) {
      if (this.roomId && blob) {
        try {
          const fd = new FormData();
          fd.append("file", blob, "background");
          // Guest sends its room-scoped invite token so the endpoint can authorize
          // the upload (Concern #3); the host authorizes via its access-token header.
          const roomToken = this.$route && this.$route.query && this.$route.query.t;
          const url = roomToken
            ? `/online/${this.roomId}/background?t=${encodeURIComponent(roomToken)}`
            : `/online/${this.roomId}/background`;
          const { data } = await axios.post(url, fd);
          const absoluteUrl = `${axios.defaults.baseURL}${data.url}`;
          if (await this._applyLocalBackground(absoluteUrl, true)) return;
        } catch (err) {
          console.error(
            "Background upload failed, falling back to inline transfer:",
            err
          );
        }
      }
      // Fallback: inline compressed data URL (heavy → data channel).
      await this._applyLocalBackground(fallbackDataUrl, false);
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = async () => {
            // P1-7 — compress, then upload to the server and share a URL instead
            // of pushing a multi-MB base64 background over the wire.
            const { blob, dataUrl } = await this._compressImage(img);
            await this.uploadBackground(blob, dataUrl);
          };
          img.onerror = () => {
            console.error("Failed to load image");
            // Reset file input on error
            if (this.$refs.fileInput) {
              this.$refs.fileInput.value = "";
            }
          };
          img.src = e.target.result;
        };
        reader.onerror = () => {
          console.error("Failed to read file");
          // Reset file input on error
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = "";
          }
        };
        reader.readAsDataURL(file);
      } else if (file.type === "application/pdf") {
        // For PDF files, you would need a PDF-to-image conversion library
        // For now, we'll show an alert
        // alert(
        //   "PDF support requires additional libraries. Please convert to image format."
        // );
        // Reset file input
        // if (this.$refs.fileInput) {
        //   this.$refs.fileInput.value = "";
        // }
        this.handlePdfUpload(file);
      }
    },
    // Lazily load the locally-bundled PDF.js only when a PDF is actually opened,
    // so it never affects the cold-load of a guest who only uses images. The
    // library is code-split into its own chunk; the worker is served from our own
    // origin (no CDN). Cached after the first load.
    async loadPdfLib() {
      if (this._pdfjsLib) return this._pdfjsLib;
      const pdfjsLib = await import(
        /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf"
      );
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || "/"}pdf.worker.min.js`;
      this._pdfjsLib = pdfjsLib;
      return pdfjsLib;
    },

    async handlePdfUpload(file) {
      try {
        const pdfjsLib = await this.loadPdfLib();
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        this.isPdfBackground = true;
        this.pdfDoc = pdf;
        this.pdfTotalPages = pdf.numPages;
        this.pdfPage = 1;
        await this.renderPdfPage(this.pdfPage);
      } catch (error) {
        console.error("Error loading PDF:", error);
        alert("Failed to load PDF file. Please try converting it to an image format.");
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      }
    },

    async renderPdfPage(pageNumber) {
      if (!this.pdfDoc) return;
      try {
        const page = await this.pdfDoc.getPage(pageNumber);
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        // Scale to a target max dimension instead of a fixed factor, so large-
        // format PDFs don't rasterize into oversized JPEGs (bounds upload size and
        // render time) while normal pages stay crisp.
        const MAX_DIM = 1800;
        const base = page.getViewport({ scale: 1 });
        const scale = Math.min(2, MAX_DIM / Math.max(base.width, base.height));
        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport: viewport }).promise;
        // P1-7 — upload the rasterized page to the server and share its URL
        // (fallback: the JPEG data URL over the data channel). `uploadBackground`
        // sets the local background + dimensions and broadcasts it.
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        canvas.toBlob(
          async (blob) => {
            await this.uploadBackground(blob, dataUrl);
          },
          "image/jpeg",
          0.8
        );
      } catch (error) {
        console.error("Error rendering PDF page:", error);
      }
    },

    async changePdfPage(newPage) {
      if (newPage < 1 || newPage > this.pdfTotalPages) return;
      this.pdfPage = newPage;
      await this.renderPdfPage(newPage);
    },
    // handlePdfPageChange(page) {
    //   if (page < 1 || page > this.pdfPages || this.pdfdata == null) return;
    //   try {
    //     const canvas = document.getElementById(`pdfPage${page}`);
    //     const img = canvas.toDataURL("image/png");
    //     this.handleFileUpload({
    //       target: { files: [dataURLtoFile(img, `pdfPage${page}.png`)] },
    //     });
    //   } catch (error) {
    //     console.error("Error handling PDF page change:", error);
    //   }
    // },

    clearBackground() {
      this.backgroundImage = null;
      this.backgroundImageConfig = null;
      this.frameBackgroundConfig = null;
      this.hardFrameConfig = null;
      this.clippingGroupConfig = null;

      // Reset content zoom and offset when clearing background
      this.contentZoom = 1;
      this.contentOffset = { x: 0, y: 0 };

      // Reset the file input to allow selecting the same file again
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }

      // Reset to default dimensions when no background
      this.backgroundDimensions = {
        width: 1920,
        height: 1080,
        naturalWidth: 1920,
        naturalHeight: 1080,
      };

      // Recalculate for default background
      this.calculateBackgroundScale();

      // Re-enable stage dragging when background is cleared
      if (this.stage) {
        const canDrag =
          !this.isGuest || (this.permissions && this.permissions.canDragCanvas);
        this.stage.draggable(canDrag && this.activeTool === "select");
      }

      // Emit canvas update for background clear
      this.emitCanvasUpdate(true);
    },

    clearCanvas() {
      this.lines = [];
      this.rectangles = [];
      this.circles = [];

      // Save to history after clearing
      this.saveToHistory();

      // Send clear event instead of empty arrays
      this.$emit("canvas-updated", {
        clearCanvas: true,
        timestamp: Date.now(),
      });
    },

    emitCanvasUpdate(force = false) {
      // Send only the newly added element instead of the entire canvas state
      const updateData = {};

      // Get the most recently added element (not yet synced)
      if (this.lines.length > 0) {
        const lastLine = this.lines[this.lines.length - 1];
        if (lastLine && !lastLine.synced) {
          updateData.newLine = { ...lastLine };
          console.log("Emitting new line:", updateData.newLine);
        }
      }

      if (this.rectangles.length > 0) {
        const lastRect = this.rectangles[this.rectangles.length - 1];
        if (lastRect && !lastRect.synced) {
          updateData.newRectangle = { ...lastRect };
          console.log("Emitting new rectangle:", updateData.newRectangle);
        }
      }

      if (this.circles.length > 0) {
        const lastCircle = this.circles[this.circles.length - 1];
        if (lastCircle && !lastCircle.synced) {
          updateData.newCircle = { ...lastCircle };
          console.log("Emitting new circle:", updateData.newCircle);
        }
      }

      // Only emit if there's something new to send
      if (Object.keys(updateData).length > 0 || force) {
        updateData.timestamp = Date.now();

        // Only send the (heavy) background when it actually changed — i.e. on a
        // forced update (upload / PDF render / clear). Ordinary stroke updates
        // must NOT re-serialize the multi-MB image every time (P0-2).
        if (force) {
          updateData.backgroundImage = this.backgroundImage
            ? this.backgroundImage.src
            : null;
          updateData.backgroundDimensions = this.backgroundDimensions;
        }

        console.log("Emitting canvas update:", updateData);
        this.$emit("canvas-updated", updateData);

        // Mark elements as synced AFTER emitting
        if (updateData.newLine && this.lines.length > 0) {
          this.lines[this.lines.length - 1].synced = true;
        }
        if (updateData.newRectangle && this.rectangles.length > 0) {
          this.rectangles[this.rectangles.length - 1].synced = true;
        }
        if (updateData.newCircle && this.circles.length > 0) {
          this.circles[this.circles.length - 1].synced = true;
        }
      } else {
        console.log("No new elements to emit");
      }
    },

    // Emit complete canvas state for undo/redo synchronization
    emitCompleteCanvasState() {
      if (this.isGuest) {
        return; // Only host can emit complete state
      }

      // P4-20 — deliberately DO NOT include the background image here.
      // Undo/redo history only snapshots lines/rectangles/circles
      // (see `saveToHistory`), so the background never changes on undo/redo and
      // re-sending it is pointless. More importantly, a `data:` background src
      // makes `HostView.handleCanvasUpdate` route this payload over the flaky
      // WebRTC data channel instead of the reliable Socket.IO relay — when that
      // channel is congested or not yet connected, the guest silently never
      // receives the undo (the "sometimes doesn't undo the guest" bug). Omitting
      // the background keeps the payload light so it always rides the socket.
      const completeState = {
        lines: this.lines.map((line) => ({ ...line, synced: true })),
        rectangles: this.rectangles.map((rect) => ({ ...rect, synced: true })),
        circles: this.circles.map((circle) => ({ ...circle, synced: true })),
        isCompleteSync: true, // Flag to indicate this is a complete state sync
        timestamp: Date.now(),
      };

      console.log("Emitting complete canvas state for undo/redo sync:", completeState);
      this.$emit("canvas-updated", completeState);
    },

    // P4-19 — object eraser. Removes the whole drawn element under the pointer.
    // The background image is a Konva.Image that is NOT tracked in the
    // lines/rectangles/circles arrays, so a hit on it resolves to no matching id
    // and is ignored — the eraser can never remove the background by construction.
    eraseAtPointer() {
      if (!this.stage) return;
      const pointer = this.stage.getPointerPosition();
      if (!pointer) return;
      const shape = this.stage.getIntersection(pointer);
      if (!shape || typeof shape.id !== "function") return;
      const id = shape.id();
      if (!id) return; // background image / untracked node → ignore
      this.eraseElementById(id);
    },

    eraseElementById(id) {
      if (!this._erasedIds) this._erasedIds = [];
      if (this._erasedIds.includes(id)) return;

      let removed = false;
      const li = this.lines.findIndex((l) => l.id === id);
      if (li !== -1) {
        this.lines.splice(li, 1);
        removed = true;
      }
      if (!removed) {
        const ri = this.rectangles.findIndex((r) => r.id === id);
        if (ri !== -1) {
          this.rectangles.splice(ri, 1);
          removed = true;
        }
      }
      if (!removed) {
        const ci = this.circles.findIndex((c) => c.id === id);
        if (ci !== -1) {
          this.circles.splice(ci, 1);
          removed = true;
        }
      }

      if (removed) {
        this._erasedIds.push(id);
        this.$nextTick(() => {
          if (this.$refs.drawingLayer) {
            this.$refs.drawingLayer.getNode().batchDraw();
          }
        });
      }
    },

    // P4-19 — broadcast an object-eraser deletion so the peer removes the same
    // elements. The payload carries no background, so `handleCanvasUpdate` routes
    // it over the reliable Socket.IO relay (never the flaky data channel).
    emitElementDelete(ids) {
      if (!ids || ids.length === 0) return;
      this.$emit("canvas-updated", {
        deletedIds: [...ids],
        timestamp: Date.now(),
      });
    },

    // Method to apply canvas updates from remote peers
    applyCanvasUpdate(canvasData) {
      console.log("Applying canvas update:", canvasData);

      // Handle canvas clear
      if (canvasData.clearCanvas) {
        console.log("Clearing canvas from remote peer");
        this.lines = [];
        this.rectangles = [];
        this.circles = [];
        this.$nextTick(() => {
          if (this.$refs.drawingLayer) {
            this.$refs.drawingLayer.getNode().batchDraw();
          }
        });
        return;
      }

      // P4-19 — object-eraser deletion from a remote peer. On the host, snapshot
      // around it so a guest-originated erase is undoable (mirrors the per-element
      // draw path); on the guest, just remove the elements.
      if (canvasData.deletedIds && canvasData.deletedIds.length > 0) {
        console.log("Applying remote erase:", canvasData.deletedIds);
        if (!this.isGuest) this.ensureCurrentStateSaved();
        const del = new Set(canvasData.deletedIds);
        this.lines = this.lines.filter((l) => !del.has(l.id));
        this.rectangles = this.rectangles.filter((r) => !del.has(r.id));
        this.circles = this.circles.filter((c) => !del.has(c.id));
        if (!this.isGuest) this.saveToHistory();
        this.$nextTick(() => {
          if (this.$refs.drawingLayer) {
            this.$refs.drawingLayer.getNode().batchDraw();
          }
        });
        return;
      }

      // Handle complete state sync (for undo/redo operations)
      if (canvasData.isCompleteSync) {
        console.log("Complete state sync for undo/redo - replacing all canvas elements");

        // Replace all canvas elements with the complete state from host
        this.lines = canvasData.lines
          ? canvasData.lines.map((line) => ({ ...line, synced: true }))
          : [];
        this.rectangles = canvasData.rectangles
          ? canvasData.rectangles.map((rect) => ({ ...rect, synced: true }))
          : [];
        this.circles = canvasData.circles
          ? canvasData.circles.map((circle) => ({ ...circle, synced: true }))
          : [];

        console.log("Applied complete state:", {
          lines: this.lines.length,
          rectangles: this.rectangles.length,
          circles: this.circles.length,
        });

        // Force re-render of the canvas
        this.$nextTick(() => {
          if (this.$refs.drawingLayer) {
            this.$refs.drawingLayer.getNode().batchDraw();
          }
        });
        return;
      }

      // Handle individual new elements (real-time updates)
      if (canvasData.newLine) {
        console.log("Adding new line from remote peer:", canvasData.newLine);

        // If this is the host receiving a guest's drawing, ensure current state is saved first
        if (!this.isGuest) {
          this.ensureCurrentStateSaved();
        }

        canvasData.newLine.synced = true; // Mark as synced to avoid conflicts
        this.lines.push({ ...canvasData.newLine });

        // If this is the host receiving a guest's drawing, save new state to history
        if (!this.isGuest) {
          this.saveToHistory();
        }
      }

      if (canvasData.newRectangle) {
        console.log("Adding new rectangle from remote peer:", canvasData.newRectangle);

        // If this is the host receiving a guest's drawing, ensure current state is saved first
        if (!this.isGuest) {
          this.ensureCurrentStateSaved();
        }

        canvasData.newRectangle.synced = true;
        this.rectangles.push({ ...canvasData.newRectangle });

        // If this is the host receiving a guest's drawing, save new state to history
        if (!this.isGuest) {
          this.saveToHistory();
        }
      }

      if (canvasData.newCircle) {
        console.log("Adding new circle from remote peer:", canvasData.newCircle);

        // If this is the host receiving a guest's drawing, ensure current state is saved first
        if (!this.isGuest) {
          this.ensureCurrentStateSaved();
        }

        canvasData.newCircle.synced = true;
        this.circles.push({ ...canvasData.newCircle });

        // If this is the host receiving a guest's drawing, save new state to history
        if (!this.isGuest) {
          this.saveToHistory();
        }
      }

      // Handle complete state sync (for initial connection or full sync) - legacy support
      if (canvasData.lines && !canvasData.newLine && !canvasData.isCompleteSync) {
        console.log("Full sync - replacing lines:", canvasData.lines.length, "lines");
        this.lines = canvasData.lines.map((line) => ({ ...line, synced: true }));
      }
      if (
        canvasData.rectangles &&
        !canvasData.newRectangle &&
        !canvasData.isCompleteSync
      ) {
        console.log(
          "Full sync - replacing rectangles:",
          canvasData.rectangles.length,
          "rectangles"
        );
        this.rectangles = canvasData.rectangles.map((rect) => ({
          ...rect,
          synced: true,
        }));
      }
      if (canvasData.circles && !canvasData.newCircle && !canvasData.isCompleteSync) {
        console.log(
          "Full sync - replacing circles:",
          canvasData.circles.length,
          "circles"
        );
        this.circles = canvasData.circles.map((circle) => ({ ...circle, synced: true }));
      }

      // Handle background dimensions sync
      if (canvasData.backgroundDimensions) {
        console.log(
          "Updating background dimensions from:",
          this.backgroundDimensions,
          "to:",
          canvasData.backgroundDimensions
        );
        this.backgroundDimensions = { ...canvasData.backgroundDimensions };
        this.calculateBackgroundScale();
      }

      // Handle background image sync
      if (canvasData.backgroundImage !== undefined) {
        if (canvasData.backgroundImage === null) {
          // Clear background
          this.isBackgroundLoading = false; // P4-17 — nothing to load
          console.log("Clearing background image");
          this.backgroundImage = null;
          this.backgroundImageConfig = null;
          this.frameBackgroundConfig = null;
          this.hardFrameConfig = null;
          this.clippingGroupConfig = null;
        } else if (canvasData.backgroundImage?.chunk) {
          // P4-17 — a new background is on the way (still transferring): overlay on.
          this.isBackgroundLoading = true;
          // Reassemble per transfer so a duplicate/dropped chunk or a second
          // transfer can't corrupt a shared buffer (P0-3).
          const { chunk, i, totalChunks, transferId } =
            canvasData.backgroundImage;
          const id = transferId || "legacy";
          let t = this.transfers[id];
          if (!t) {
            t = this.transfers[id] = {
              parts: new Array(totalChunks),
              got: 0,
              timer: setTimeout(() => {
                delete this.transfers[id];
                this.isBackgroundLoading = false; // P4-17 — abandon overlay on timeout
                console.warn("Image transfer timed out, discarding:", id);
              }, 30000),
            };
          }
          if (t.parts[i] === undefined) {
            t.parts[i] = chunk;
            t.got++;
          }
          if (t.got === totalChunks) {
            clearTimeout(t.timer);
            const base64Data = t.parts.join("");
            delete this.transfers[id];
            const img = new Image();
            img.onload = () => {
              this.backgroundImage = img;

              // Update background dimensions based on actual image
              this.backgroundDimensions = {
                width: img.naturalWidth,
                height: img.naturalHeight,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
              };

              console.log(
                "Background image loaded from chunks, dimensions:",
                this.backgroundDimensions
              );
              this.calculateBackgroundScale();
              this.updateBackgroundImageSize();
              this.$nextTick(() => {
                if (this.$refs.drawingLayer) {
                  this.$refs.drawingLayer.getNode().batchDraw();
                }
              });
              this.isBackgroundLoading = false; // P4-17
            };
            img.onerror = () => {
              this.isBackgroundLoading = false; // P4-17
            };
            img.src = base64Data;
          }
        } else {
          // Set new background — either a server URL (P1-7) or a base64 data URL
          // (P0-3 fallback). `crossOrigin` keeps the canvas untainted when the
          // image is served cross-origin (the server sends CORP: cross-origin).
          console.log(
            "Loading background image:",
            canvasData.backgroundImage.substring(0, 50) + "..."
          );
          this.isBackgroundLoading = true; // P4-17
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            this.backgroundImage = img;

            // Update background dimensions based on actual image
            this.backgroundDimensions = {
              width: img.naturalWidth,
              height: img.naturalHeight,
              naturalWidth: img.naturalWidth,
              naturalHeight: img.naturalHeight,
            };

            console.log(
              "Background image loaded, dimensions:",
              this.backgroundDimensions
            );
            this.calculateBackgroundScale();
            this.updateBackgroundImageSize();
            this.$nextTick(() => {
              if (this.$refs.drawingLayer) {
                this.$refs.drawingLayer.getNode().batchDraw();
              }
            });
            this.isBackgroundLoading = false; // P4-17
          };
          img.onerror = () => {
            this.isBackgroundLoading = false; // P4-17
          };
          img.src = canvasData.backgroundImage;
        }
      }

      // Force re-render of the canvas
      this.$nextTick(() => {
        if (this.$refs.drawingLayer) {
          this.$refs.drawingLayer.getNode().batchDraw();
        }
      });

      // Emit event to notify parent that canvas state changed (for undo/redo button updates)
      if (!this.isGuest) {
        this.$emit("canvas-state-changed");
      }
    },

    // Emit zoom update for synchronization (Host only)
    emitZoomUpdate() {
      if (this.isGuest) {
        return; // Guests should not emit zoom updates
      }

      const zoomData = {
        scaleX: this.stageConfig.scaleX,
        scaleY: this.stageConfig.scaleY,
        x: this.stageConfig.x,
        y: this.stageConfig.y,
        timestamp: Date.now(),
      };

      console.log("Emitting zoom update:", zoomData);
      this.$emit("zoom-updated", zoomData);
    },

    // Apply zoom update from host (Guest only)
    applyZoomUpdate(zoomData) {
      if (!this.isGuest) {
        return; // Only guests should apply zoom updates from host
      }

      console.log("Applying zoom update from host:", zoomData);

      const stage = this.stage;
      if (!stage) {
        console.warn("Stage not available for zoom update");
        return;
      }

      // Handle content zoom updates (for background images)
      if (zoomData.contentZoom !== undefined && zoomData.contentOffset !== undefined) {
        this.applyContentZoomUpdate(zoomData);
        return;
      }

      // Handle stage zoom updates (for no background image)
      // Apply zoom and position changes
      stage.scale({ x: zoomData.scaleX, y: zoomData.scaleY });
      stage.position({ x: zoomData.x, y: zoomData.y });

      // Update our config to match
      this.stageConfig.scaleX = zoomData.scaleX;
      this.stageConfig.scaleY = zoomData.scaleY;
      this.stageConfig.x = zoomData.x;
      this.stageConfig.y = zoomData.y;

      // Force canvas redraw
      this.$nextTick(() => {
        if (this.$refs.drawingLayer) {
          this.$refs.drawingLayer.getNode().batchDraw();
        }
      });
    },

    // Method to get current canvas state for synchronization
    getCurrentCanvasState() {
      return {
        lines: this.lines.map((line) => ({ ...line, synced: true })),
        rectangles: this.rectangles.map((rect) => ({ ...rect, synced: true })),
        circles: this.circles.map((circle) => ({ ...circle, synced: true })),
        backgroundImage: this.backgroundImage ? this.backgroundImage.src : null,
        backgroundDimensions: this.backgroundDimensions,
        timestamp: Date.now(),
      };
    },

    // Method to manually recalculate stage size (can be called from parent)
    recalculateStage() {
      this.updateStageSize();
    },

    // Check if guest has permission to use a specific tool
    checkToolPermission(tool) {
      if (!this.isGuest || !this.permissions) {
        return true; // Host always has permission
      }

      switch (tool) {
        case "pen":
          return this.permissions.canDraw;
        case "undo":
          return this.permissions.canUndo;
        case "redo":
          return this.permissions.canRedo;
        case "eraser":
          return this.permissions.canErase;
        case "rectangle":
        case "circle":
          return this.permissions.canAddShapes;
        case "select":
          return this.permissions.canSelect;
        default:
          return false;
      }
    },

    triggerFileUpload() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    toggleControls() {
      this.showControls = !this.showControls;
    },

    toggleFullscreen() {
      // Emit event to parent to hide all sidebars
      this.$emit("toggle-fullscreen");
    },

    toggleSidebars() {
      this.$emit("toggle-sidebars");
    },
  },
};
</script>
<style scoped>
.canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  overflow: hidden;
}

/* P4-17 — background-load overlay (disables canvas + spinner) */
.bg-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  cursor: wait;
}

.bg-loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 28px 36px;
  border-radius: 16px;
  background: rgba(30, 30, 30, 0.85);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.bg-loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.15);
  border-top-color: #de39a5;
  border-radius: 50%;
  animation: bg-loading-spin 1s linear infinite;
}

.bg-loading-text {
  margin: 0;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

@keyframes bg-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Sidebar Toggle Button */
.sidebar-toggle {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: rgba(40, 167, 69, 0.9);
  color: white;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.sidebar-toggle:hover {
  background-color: rgba(40, 167, 69, 1);
  transform: translateX(-50%) scale(1.1);
}

/* Controls Toggle Button */
.controls-toggle {
  position: absolute;
  top: 10px;
  right: 50%;
  transform: translateX(50%);
  z-index: 1001;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 123, 255, 0.9);
  color: white;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.controls-toggle:hover {
  background-color: rgba(0, 123, 255, 1);
  transform: translateX(50%) scale(1.1);
}

/* Simplified Controls */
.background-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  display: flex;
  gap: 4px;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 6px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.zoom-controls {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 6px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.background-controls.hidden,
.zoom-controls.hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(-10px);
}

/* Unified Control Button Style */
.control-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #ffffff;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #495057;
}

.control-btn:hover {
  background-color: #f8f9fa;
  border-color: #007bff;
  color: #007bff;
}

.control-btn:active {
  transform: scale(0.95);
}

.zoom-level {
  font-size: 10px;
  font-weight: 600;
  color: #495057;
  min-width: 32px;
  text-align: center;
  padding: 0 4px;
}

.reset-btn {
  border-color: #28a745;
  color: #28a745;
}

.reset-btn:hover {
  background-color: #28a745;
  color: white;
  border-color: #28a745;
}

.clear-btn {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.clear-btn:hover {
  background-color: #c82333;
  border-color: #c82333;
}

.control-btn.fullscreen-btn {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.control-btn.fullscreen-btn:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

/* Ensure the Konva stage takes full width/height but doesn't interfere with controls */
.canvas-container >>> .konvajs-content {
  width: 100% !important;
  height: 100% !important;
  position: relative;
  z-index: 1;
}

.canvas-container >>> canvas {
  position: relative;
  z-index: 1;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .sidebar-toggle,
  .controls-toggle {
    display: none;
  }
}

/* Desktop adjustments */
@media (min-width: 769px) {
  /* Smaller controls for larger screens */
  .background-controls,
  .zoom-controls {
    padding: 4px;
  }

  .control-btn {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }

  .zoom-level {
    font-size: 9px;
    min-width: 28px;
  }
}

/* Frame Violation Feedback */
.frame-violation-feedback {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1002;
  background-color: rgba(220, 53, 69, 0.95);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
  animation: frameViolationPulse 0.5s ease-in-out;
  pointer-events: none;
}

.frame-violation-feedback i {
  font-size: 16px;
  animation: frameViolationShake 0.3s ease-in-out;
}

@keyframes frameViolationPulse {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.05);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes frameViolationShake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-2px);
  }
  75% {
    transform: translateX(2px);
  }
}
</style>
